<template>
  <vd-page-dialog
    class="classes-catalog-import-page"
    :title="displayConflictWarning ? 'Some classes already exists' : 'Classes catalog'"
    :subtitle="
      displayConflictWarning
        ? 'The following classes already exists in your game. Select the one you want to override:'
        : 'Select the classes you want to import:'
    "
    :back-link="classesListLink"
    :hide-close-button="!!displayConflictWarning"
    :type="displayConflictWarning ? 'error' : 'info'"
    :loading="queryClassesLoading || deleteLoading"
  >
    <template v-if="!displayConflictWarning" #page-actions>
      <VdInput v-model="searchTerm" class="search-input" placeholder="Looking for something...">
        <template #prepend>
          <VdIcon store="fa" name="search" />
        </template>
      </VdInput>
    </template>

    <div v-if="displayConflictWarning" class="conflict-warning">
      <el-table
        ref="classesToOverrideTableRef"
        :data="classesInConflict"
        @selection-change="onClassesToOverrideSelectionChange"
        @row-click="onClassesToOverrideRowClicked"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column prop="displayName" label="Display name" />
        <el-table-column prop="technicalName" label="Technical name" />
      </el-table>
    </div>

    <div v-else class="classes-list" data-cy="classes-catalog">
      <keep-alive v-if="finished && filteredClasses.length === 0">
        <vd-empty :title="searchTerm ? 'not found' : 'no class in catalog'">
          <div v-if="searchTerm">
            <p>Your search for a Class has no results</p>
          </div>
          <div v-else>
            <p>Sorry, there is no data.</p>
          </div>
        </vd-empty>
      </keep-alive>
      <div v-else>
        <div v-for="item in filteredClasses" :key="item.technicalName" class="class-row">
          <VdCheckbox
            v-if="canWriteClasses"
            :model-value="isSelected(item.technicalName)"
            class="class-row-checkbox"
            @update:model-value="toggleClass(item)"
          />
          <ClassesListItem
            :value="item"
            :game-id="gameId"
            :version-id="versionId"
            scope="catalog"
            class="class-row-item"
            @delete-from-catalog="onCatalogDelete"
            @click="toggleClass(item)"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <vd-button
        v-if="displayConflictWarning"
        :type="classesToOverride.length ? 'primary' : 'secondary'"
        skin="blue"
        size="large"
        data-cy="import-class-override"
        @click="closeConflictWarning"
      >
        <span v-if="classesToOverride.length">Override selected classes</span>
        <span v-else>Skip those classes</span>
      </vd-button>
      <vd-button
        v-else-if="canWriteClasses"
        :loading="importClassesLoading"
        type="primary"
        size="large"
        data-cy="import-class"
        @click="onSubmit"
      >
        Import
      </vd-button>
    </template>
  </vd-page-dialog>
</template>

<script lang="ts">
/**
 * This page represents the catalog classes that can be imported to the game.
 */

import { VdInput, VdCheckbox } from "@voodoo.io/voodoo-ui";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { ElTable } from "element-plus";
import { computed, defineComponent, ref } from "vue";
import { useRouter } from "vue-router";

import {
  useClassesCatalogImportPageQuery,
  ClassesListPageClassInfoFragment,
  useClassesCatalogImportPageImportMutation,
  useClassesCatalogImportPageDeleteMutation,
  useMutationResponse,
  queryNames,
} from "@/graphql";
import useMessages from "@/hooks/messages";
import useNotifications from "@/hooks/notifications";
import useSearchFilter from "@/hooks/search-filter";
import usePermissions from "@/hooks/use-permissions";
import ClassesListItem from "@/pages/games/GameDisplay/children/classes/ClassesList/components/ClassesListItem.vue";
import links from "@/router/links";

export default defineComponent({
  name: "ClassesCatalogImportPage",

  components: {
    ClassesListItem,
    VdIcon,
    VdInput,
    VdCheckbox,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },
    versionId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const router = useRouter();
    const { notifyError } = useNotifications();
    const { confirm } = useMessages();
    const { canWriteClasses } = usePermissions();

    /**
     * Fetch the catalog classes
     */
    const { result, loading: queryClassesLoading } = useClassesCatalogImportPageQuery(
      {
        gameId: props.gameId,
      },
      { fetchPolicy: "network-only" }
    );

    const classes = computed(() => result.value?.game?.catalogClasses ?? []);

    const finished = computed(() => !queryClassesLoading.value);

    /**
     * Filter the classes from the searchTerm
     */

    const { searchTerm, filteredItems: filteredClasses } = useSearchFilter(classes, [
      "displayName",
      "technicalName",
      "description",
    ]);

    /**
     * Handle the selection of the classes that will be imported to the game.
     */
    const classesSelection = ref<ClassesListPageClassInfoFragment[]>([]);

    const isSelected = (classId: string) => classesSelection.value.some((o) => o.technicalName === classId);
    const toggleClass = (clazz: ClassesListPageClassInfoFragment) => {
      if (isSelected(clazz.technicalName)) {
        classesSelection.value = classesSelection.value.filter((o) => o.technicalName !== clazz.technicalName);
      } else {
        classesSelection.value = [...classesSelection.value, clazz];
      }
    };

    const onSelectionChange = (rows: ClassesListPageClassInfoFragment[]) => {
      classesSelection.value = [...rows];
    };

    /**
     * The location to redirect when clicking on the close button on the dialog
     */

    const classesListLink = {
      name: links.games.classes.list,
      params: { gameId: props.gameId, versionId: props.versionId },
    };

    /**
     * Handle the class deletion
     */

    const { mutate: deleteFromCatalog, loading: deleteLoading } = useClassesCatalogImportPageDeleteMutation({
      refetchQueries: [queryNames.ClassesCatalogImportPage],
    });

    const onCatalogDelete = async (classId: string) => {
      const confirmed = await confirm("Do you really want to delete this class from the Catalog?");
      if (confirmed) {
        const response = await deleteFromCatalog({ classId });
        useMutationResponse(response, (resp) => resp?.deleteCatalogClass);
      }
    };

    /**
     * Handle the classes import
     */

    const { mutate: importClasses, loading: importClassesLoading } = useClassesCatalogImportPageImportMutation({
      refetchQueries: [queryNames.ClassesListPage],
    });

    const classesInConflict = ref<{ displayName: string; technicalName: string }[]>([]);
    const displayConflictWarning = ref(false);
    const classesToOverride = ref<ClassesListPageClassInfoFragment[]>([]);
    const classesToOverrideTableRef = ref<InstanceType<typeof ElTable>>();

    const onSubmit = async () => {
      const selection = classesSelection.value.map((clazz) => clazz.technicalName);
      if (selection.length) {
        const response = await importClasses({
          gameId: props.gameId,
          versionId: props.versionId,
          classIds: selection,
          override: false,
        });
        const importResults = useMutationResponse(response, (resp) => resp?.importClassesFromCatalog);
        /**
         * If some features are in conflict, get their id and alert the user
         */
        if (!importResults?.success) {
          classesInConflict.value =
            importResults?.errorDetails
              .filter((error) => {
                if (error.isConflict) {
                  return true;
                }
                // TODO maybe concatenate errors to avoid spamming with notifs, not sure though.
                notifyError(error.errorMessage, {
                  dangerouslyUseHTMLString: true,
                  duration: 15 * 1000,
                });
                return false;
              })
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              .map((error) => ({ technicalName: error?.technicalName ?? "", displayName: error?.className ?? "" })) ??
            [];
          if (classesInConflict.value.length) {
            displayConflictWarning.value = true;
            return;
          }
        }
      }
      router.push({ name: links.games.classes.list });
    };

    const onClassesToOverrideSelectionChange = (rows: ClassesListPageClassInfoFragment[]) => {
      classesToOverride.value = [...rows];
    };

    const onClassesToOverrideRowClicked = (row: ClassesListPageClassInfoFragment) => {
      if (classesToOverrideTableRef.value) {
        classesToOverrideTableRef.value.toggleRowSelection(row, false); // XXX CHECKME
      }
    };

    const closeConflictWarning = async () => {
      const response = await importClasses({
        gameId: props.gameId,
        versionId: props.versionId,
        classIds: classesToOverride.value.map((clazz) => clazz.technicalName),
        override: true,
      });
      const importResults = useMutationResponse(response, (resp) => resp?.importClassesFromCatalog);
      if (importResults?.success) {
        router.push({ name: links.games.classes.list, params: { gameId: props.gameId, versionId: props.versionId } });
      } else {
        (importResults?.errorDetails ?? []).forEach((error) => {
          notifyError(error.errorMessage, {
            dangerouslyUseHTMLString: true,
            duration: 15 * 1000,
          });
        });
      }
    };

    return {
      queryClassesLoading,
      deleteLoading,
      finished,
      classes,
      isSelected,
      toggleClass,
      searchTerm,
      filteredClasses,
      onSelectionChange,
      classesListLink,
      importClassesLoading,
      onSubmit,
      onCatalogDelete,
      closeConflictWarning,
      classesInConflict,
      displayConflictWarning,
      classesToOverride,
      onClassesToOverrideSelectionChange,
      onClassesToOverrideRowClicked,
      classesToOverrideTableRef,
      canWriteClasses,
    };
  },
});
</script>

<style lang="scss">
.classes-catalog-import-page {
  .el-card__body {
    &:hover {
      background-color: $foggy-blue;
    }
  }

  .classes-table {
    @extend %card;

    .name-link {
      font-size: 16px;
      padding-bottom: 0;
    }
    .technical-name {
      font-size: 12px;
      font-family: monospace;
    }
  }

  .action-buttons {
    display: flex;
    justify-content: center;
    flex-wrap: nowrap;
    align-items: center;
    margin: 30px;
  }

  .class-row {
    display: flex;
    align-items: center;

    .class-row-checkbox {
      margin-right: 20px;
    }

    .class-row-item {
      width: 100%;
    }
  }
}
</style>
